import { useCallback, useRef } from "react";
import style from "./style.module.css";

interface GradientHandleProps {
  x: number;
  y: number;
  onDrag: (x: number, y: number) => void;
  onDragEnd?: () => void;
  onClick?: () => void;
  color?: string;
  size?: number;
  selected?: boolean;
}

const GradientHandle = ({
  x,
  y,
  onDrag,
  onDragEnd,
  onClick: onHandleClick,
  color,
  size = 14,
  selected = false
}: GradientHandleProps) => {
  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const gRef = useRef<SVGGElement | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isDragging.current = true;
      didDrag.current = false;
      (e.target as Element).setPointerCapture(e.pointerId);
      onHandleClick?.();
    },
    [onHandleClick]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging.current) return;
      e.preventDefault();
      e.stopPropagation();
      didDrag.current = true;

      const svg = gRef.current?.closest("svg[data-gradient-overlay]");
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      // Account for the <g> transform offset stored as a data attribute
      const offsetX = parseFloat(svg.getAttribute("data-offset-x") || "0");
      const offsetY = parseFloat(svg.getAttribute("data-offset-y") || "0");
      const newX = e.clientX - rect.left - offsetX;
      const newY = e.clientY - rect.top - offsetY;
      onDrag(newX, newY);
    },
    [onDrag]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging.current) return;
      const wasDrag = didDrag.current;
      isDragging.current = false;
      didDrag.current = false;
      (e.target as Element).releasePointerCapture(e.pointerId);
      onDragEnd?.();

      // If it was just a click (no movement), fire onClick
      if (!wasDrag && onHandleClick) {
        onHandleClick();
      }
    },
    [onDragEnd, onHandleClick]
  );

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const halfSize = size / 2;
  const strokeColor = selected ? "#2d70cf" : "#ffffff";
  const strokeWidth = selected ? 2.5 : 2;

  return (
    <g ref={gRef}>
      {/* Larger invisible hit area */}
      <circle
        cx={x}
        cy={y}
        r={halfSize + 4}
        fill="transparent"
        className={style.handleHitArea}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onClick={handleClick}
      />
      {/* Selected ring */}
      {selected && (
        <circle
          cx={x}
          cy={y}
          r={halfSize + 2}
          fill="none"
          stroke="#ffffff"
          strokeWidth={2}
          style={{ pointerEvents: "none" }}
        />
      )}
      {/* Visible handle */}
      <circle
        cx={x}
        cy={y}
        r={halfSize}
        fill={color || "#ffffff"}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        className={style.handle}
        style={{ pointerEvents: "none" }}
      />
      {/* Inner border for contrast */}
      <circle
        cx={x}
        cy={y}
        r={halfSize - 1}
        fill="none"
        stroke="rgba(0,0,0,0.15)"
        strokeWidth={0.5}
        style={{ pointerEvents: "none" }}
      />
    </g>
  );
};

export default GradientHandle;
